import { useEffect, useRef } from 'react';
import { stats, trustItems, studyCountries, processSteps, whyUs, testimonials, faqs, valueAddedServices, WHATSAPP_URL } from '../data/siteData';
import { ValueAddedIcon, getIconKey, getAccentColor } from '../components/ValueAddedIcons';
import { SectionHeader, FAQItem, CTASection, TestiCard } from '../components/UI';

// Flag images for destination preview grid (all 35 flags)
const flagImgHome = {
  'Australia':      '/flags/btn_australia.png',
  'Austria':        '/flags/btn_austria.png',
  'Belgium':        '/flags/btn_belgium.png',
  'Brazil':         '/flags/btn_brazil.png',
  'Canada':         '/flags/btn_canada.png',
  'Greece':         '/flags/btn_greece.png',
  'Hungary':        '/flags/btn_hungary.png',
  'Ireland':        '/flags/btn_ireland.png',
  'Italy':          '/flags/btn_italy.png',
  'Japan':          '/flags/btn_japan.png',
  'Malaysia':       '/flags/btn_malaysia.png',
  'Portugal':       '/flags/btn_portugal.png',
  'Singapore':      '/flags/btn_singapore.png',
  'South Africa':   '/flags/btn_south_africa.png',
  'South Korea':    '/flags/btn_south_korea.png',
  'Spain':          '/flags/btn_spain.png',
  'Sweden':         '/flags/btn_sweden.png',
  'Switzerland':    '/flags/btn_switzerland.png',
  'Turkey':         '/flags/btn_turkey.png',
  'Argentina':      '/flags/btn_argentina.png',
  'China':          '/flags/btn_china.png',
  'Czech Republic': '/flags/btn_czech_republic.png',
  'Denmark':        '/flags/btn_denmark.png',
  'Finland':        '/flags/btn_finland.png',
  'France':         '/flags/btn_france.png',
  'Germany':        '/flags/btn_germany.png',
  'Mexico':         '/flags/btn_mexico.png',
  'Netherlands':    '/flags/btn_netherlands.png',
  'New Zealand':    '/flags/btn_new_zealand.png',
  'Norway':         '/flags/btn_norway.png',
  'Poland':         '/flags/btn_poland.png',
  'UAE':            '/flags/btn_uae.png',
  'UK':             '/flags/btn_united_kingdom.png',
  'USA':            '/flags/btn_usa.png',
};

function Counter({ target, suffix = '', duration = 2000 }) {
  const ref = useRef(null);
  const animated = useRef(false);
  useEffect(() => {
    const el = ref.current;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !animated.current) {
        animated.current = true;
        let start = 0;
        const step = target / (duration / 16);
        const timer = setInterval(() => {
          start += step;
          if (start >= target) { start = target; clearInterval(timer); }
          el.textContent = Math.floor(start) + suffix;
        }, 16);
      }
    }, { threshold: 0.5 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [target, suffix, duration]);
  return <span ref={ref}>0{suffix}</span>;
}

function ProgressBar({ label, sublabel, pct, color }) {
  const barRef = useRef(null);
  useEffect(() => {
    const el = barRef.current;
    setTimeout(() => { if (el) el.style.width = pct + '%'; }, 600);
  }, [pct]);
  return (
    <div>
      <div className="flex justify-between text-xs text-white/70 font-medium mb-1.5"><span>{label}</span><span>{sublabel}</span></div>
      <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
        <div ref={barRef} style={{ width: '0%', transition: 'width 1.5s cubic-bezier(0.4,0,0.2,1)' }} className={`h-full rounded-full ${color}`}></div>
      </div>
    </div>
  );
}

export default function Home({ setPage }) {
  const go = (p) => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  return (
    <div>
      {/* ── HERO ── */}
      <section className="min-h-screen bg-navy relative flex items-center overflow-hidden">
        {/* Real office photo — full bleed background */}
        <div className="absolute inset-0 z-0">
          <img
            src="/hero-office.jpg"
            alt="Travizon consultation office"
            className="w-full h-full object-cover object-center"
            draggable="false"
          />
          {/* Dark overlay so text stays readable */}
          <div className="absolute inset-0" style={{background:'linear-gradient(105deg, rgba(7,21,38,0.93) 0%, rgba(7,21,38,0.82) 45%, rgba(7,21,38,0.55) 75%, rgba(7,21,38,0.35) 100%)'}}></div>
          {/* Teal/gold colour tint layer */}
          <div className="absolute inset-0" style={{background:'radial-gradient(ellipse 60% 70% at 15% 50%, rgba(26,122,138,0.18) 0%, transparent 65%), radial-gradient(ellipse 40% 50% at 90% 80%, rgba(201,168,76,0.10) 0%, transparent 55%)'}}></div>
        </div>
        {/* Subtle grid texture on top */}
        <div className="absolute inset-0 hero-grid-bg pointer-events-none z-[1]" style={{opacity:0.25}}></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 w-full grid grid-cols-1 lg:grid-cols-2 gap-14 items-center pt-28 pb-16 relative z-[2]">
          <div>
            <div className="inline-flex items-center gap-2 bg-gold/12 border border-gold/30 rounded-full px-4 py-2 mb-7 animate-fade-up delay-100">
              <span className="w-2 h-2 rounded-full bg-gold animate-pulse"></span>
              <span className="text-[0.75rem] font-semibold tracking-widest uppercase text-gold-light">Lahore's Trusted Migration Consultancy</span>
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-semibold text-white leading-[1.1] mb-5 animate-fade-up delay-200">
              Explore. Study. <em className="not-italic text-teal-light">Settle.</em>
            </h1>
            <p className="text-white/65 text-lg max-w-[500px] mb-6 leading-relaxed animate-fade-up delay-300">
              35+ study destinations. Skilled migration. Digital nomad visas. Visit visas. Accommodation. Everything you need to move abroad — legally and confidently.
            </p>
            <div className="flex flex-wrap gap-2 mb-8 animate-fade-up delay-300">
              {['Study Abroad 35+','Scholarships','Skilled Migration','Digital Nomad Visas','Insurance','Airport Pickup'].map(tag => (
                <span key={tag} className="inline-flex items-center gap-1.5 text-[0.72rem] font-semibold text-teal bg-teal/10 border border-teal/20 rounded-full px-3 py-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-teal flex-shrink-0"></span>{tag}
                </span>
              ))}
            </div>
            <div className="flex flex-wrap gap-4 mb-10 animate-fade-up delay-400">
              <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" className="btn-primary text-base px-8 py-4">
                <svg viewBox="0 0 24 24" className="w-5 h-5 fill-white flex-shrink-0"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347"/></svg>
                Free WhatsApp Consultation
              </a>
              <button onClick={() => go('services')} className="btn-outline text-base px-8 py-4">Explore Services</button>
            </div>
            <div className="flex flex-wrap gap-8 animate-fade-up delay-500">
              {stats.slice(0,3).map(({ n, s, label }) => (
                <div key={label}>
                  <div className="font-display text-4xl font-bold text-white leading-none"><Counter target={n} suffix={s} /></div>
                  <div className="text-xs text-white/45 mt-1">{label}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="hidden lg:flex justify-center">
            <div className="relative w-full max-w-[420px]" style={{animation:'float 6s ease-in-out infinite'}}>
              <style>{`@keyframes float{0%,100%{transform:translateY(0)}50%{transform:translateY(-12px)}}`}</style>
              <div className="bg-white/[0.07] backdrop-blur-2xl border border-white/15 rounded-3xl p-7 shadow-[0_24px_80px_rgba(0,0,0,0.3)]">
                {/* Card header */}
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-teal to-teal-light flex items-center justify-center shadow-[0_4px_14px_rgba(26,122,138,0.45)] flex-shrink-0">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/>
                      <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/>
                    </svg>
                  </div>
                  <div>
                    <div className="font-display text-white text-lg font-semibold leading-tight">Your Global Journey</div>
                    <div className="text-white/45 text-[0.68rem] mt-0.5">Application tracker</div>
                  </div>
                  {/* Live dot */}
                  <div className="ml-auto flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                    <span className="text-[0.6rem] text-emerald-400 font-semibold">LIVE</span>
                  </div>
                </div>
                <div className="space-y-4">
                  <ProgressBar label="University Application" sublabel="UK, Canada" pct={85} color="bg-gradient-to-r from-teal to-teal-light" />
                  <ProgressBar label="Visa Documentation" sublabel="In Review" pct={72} color="bg-gradient-to-r from-gold to-gold-light" />
                  <ProgressBar label="Scholarship Matching" sublabel="Shortlisted" pct={60} color="bg-gradient-to-r from-blue-500 to-blue-400" />
                </div>
                {/* Bottom status row */}
                <div className="mt-5 pt-4 border-t border-white/10 flex items-center justify-between">
                  <div className="flex items-center gap-1.5 text-[0.68rem] text-white/40">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                    Updated just now
                  </div>
                  <div className="text-[0.68rem] font-semibold text-teal-light">3 Active Cases</div>
                </div>
              </div>
              {/* ── VISA APPROVED badge (top-right) ── */}
              <div className="absolute -top-6 -right-6 shadow-[0_8px_32px_rgba(16,185,129,0.25)]" style={{animation:'float 6s ease-in-out infinite 1.5s'}}>
                <div className="bg-white rounded-2xl p-3.5 flex items-center gap-3 min-w-[170px]">
                  {/* Green pulse icon */}
                  <div className="relative flex-shrink-0">
                    <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center shadow-[0_4px_12px_rgba(16,185,129,0.4)]">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                        <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
                      </svg>
                    </div>
                    {/* Pulse ring */}
                    <span className="absolute -top-0.5 -right-0.5 flex h-3 w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60"></span>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
                    </span>
                  </div>
                  <div>
                    <div className="text-[0.62rem] font-semibold tracking-widest uppercase text-emerald-600 leading-none mb-0.5">Visa Approved</div>
                    <div className="font-body font-bold text-navy text-sm leading-tight">UK Student Visa ✓</div>
                    <div className="text-[0.6rem] text-gray-400 mt-0.5">Just now · 🇬🇧</div>
                  </div>
                </div>
              </div>

              {/* ── UCL ADMISSION badge (bottom-left) ── */}
              <div className="absolute -bottom-6 -left-6 shadow-[0_8px_32px_rgba(201,168,76,0.22)]" style={{animation:'float 6s ease-in-out infinite 3s'}}>
                <div className="bg-white rounded-2xl p-3.5 flex items-center gap-3 min-w-[195px]">
                  {/* Gold cap icon */}
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gold to-gold-light flex items-center justify-center flex-shrink-0 shadow-[0_4px_12px_rgba(201,168,76,0.35)]">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                      <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
                      <path d="M6 12v5c3 3 9 3 12 0v-5"/>
                    </svg>
                  </div>
                  <div>
                    <div className="text-[0.62rem] font-semibold tracking-widest uppercase text-amber-600 leading-none mb-0.5">Offer Letter</div>
                    <div className="font-body font-bold text-navy text-sm leading-tight">UCL London 🎓</div>
                    <div className="text-[0.6rem] text-gray-400 mt-0.5">MSc Data Science · 2024</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── STATS BAR ── */}
      <div className="bg-navy-deep border-y border-teal/20 py-6">
        <div className="max-w-7xl mx-auto px-4 grid grid-cols-3 sm:grid-cols-6 gap-4 text-center">
          {stats.map(({ n, s, label }) => (
            <div key={label}>
              <div className="font-display text-3xl font-semibold text-teal-light leading-none"><Counter target={n} suffix={s} /></div>
              <div className="text-[0.62rem] text-white/40 uppercase tracking-wide mt-1 font-semibold">{label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── TRUST BAR ── */}
      <div className="bg-gray-50 border-b border-gray-200 py-4">
        <div className="max-w-7xl mx-auto px-4 flex flex-wrap items-center justify-center gap-6">
          {trustItems.map(({ label }) => (
            <div key={label} className="flex items-center gap-2 text-gray-500 text-sm font-semibold">
              <div className="w-1.5 h-1.5 rounded-full bg-teal flex-shrink-0"></div>
              {label}
            </div>
          ))}
        </div>
      </div>

      {/* ── SERVICES OVERVIEW ── */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <SectionHeader tag="What We Offer" title="All Services, One Team" subtitle="From study abroad to skilled migration to accommodation — complete support for every step of your journey." />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { icon: <svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M12 14l9-5-9-5-9 5 9 5z"/><path d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z"/></svg>, title: 'Study Abroad', sub: '35+ Countries', page: 'services' },
              { icon: <svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16"/></svg>, title: 'Skilled Migration', sub: '5 Pathways', page: 'migration' },
              { icon: <svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>, title: 'Digital Nomad', sub: '7 Destinations', page: 'services' },
              { icon: <svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M5 17H3a2 2 0 01-2-2V5a2 2 0 012-2h11a2 2 0 012 2v3m3 7a2 2 0 01-2 2H9a2 2 0 01-2-2V9a2 2 0 012-2h10a2 2 0 012 2v8z"/></svg>, title: 'Visit Visa', sub: '9 Countries', page: 'services' },
              { icon: <svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>, title: 'Accommodation', sub: '13 Countries', page: 'services' },
              { icon: <svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>, title: 'Value Added', sub: '7 Services', page: 'value-added' },
            ].map(({ icon, title, sub, page }) => (
              <button key={title} onClick={() => go(page)} className="bg-gray-50 border border-gray-100 rounded-2xl p-5 text-center hover:border-teal hover:bg-teal/5 hover:-translate-y-1 transition-all duration-300 group flex flex-col items-center">
                <div className="w-12 h-12 rounded-xl bg-teal/8 flex items-center justify-center mb-3 text-teal group-hover:bg-teal/15 transition-colors">{icon}</div>
                <div className="font-body font-semibold text-navy text-xs mb-1">{title}</div>
                <div className="text-[0.65rem] text-teal font-semibold">{sub}</div>
              </button>
            ))}
          </div>
          <div className="text-center mt-8">
            <button onClick={() => go('services')} className="btn-outline-dark">View All Services →</button>
          </div>
        </div>
      </section>

      {/* ── DESTINATIONS ── */}
      <section className="py-20 bg-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <SectionHeader tag="Study Abroad" title="35+ Study Destinations" subtitle="We support student visa applications across 35 countries. Click any country to start your enquiry." />
          <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-3">
            {studyCountries.slice(0,18).map(d => (
              <a key={d.name} href={`https://wa.me/923084715628?text=Hi, I want to enquire about Study in ${encodeURIComponent(d.name)}`} target="_blank" rel="noreferrer"
                className="group bg-white border border-gray-100 rounded-2xl overflow-hidden hover:border-teal/50 hover:-translate-y-1 hover:shadow-[0_8px_24px_rgba(11,31,58,0.1)] transition-all duration-200 cursor-pointer flex flex-col">
                <div className="h-16 w-full overflow-hidden flex-shrink-0 bg-gray-50">
                  {flagImgHome[d.name] ? (
                    <img src={flagImgHome[d.name]} alt={d.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" draggable="false" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-3xl">{d.flag}</div>
                  )}
                </div>
                <div className="px-2 py-2 text-center">
                  <div className="font-body font-bold text-navy text-[0.62rem] leading-tight">{d.name}</div>
                </div>
              </a>
            ))}
          </div>
          <div className="text-center mt-6">
            <button onClick={() => go('destinations')} className="btn-outline-dark">View All 35+ Destinations →</button>
          </div>
        </div>
      </section>

      {/* ── PROCESS + WHY ── */}
      <section className="py-20 bg-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14">
            <div>
              <span className="section-tag" style={{background:'rgba(201,168,76,0.15)',color:'#E8C97A'}}>How It Works</span>
              <h2 className="font-display text-4xl sm:text-5xl text-white font-semibold mt-1 mb-4">Our Process</h2>
              <p className="text-white/55 mb-8">Five clear steps from your first message to boarding your flight.</p>
              <div className="flex flex-col">
                {processSteps.map((s, i) => (
                  <div key={s.n} className="flex gap-4">
                    <div className="flex flex-col items-center w-8 flex-shrink-0">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-display text-sm font-semibold ${s.highlight ? 'bg-teal text-white' : 'bg-teal/15 border-2 border-teal text-teal'}`}>{s.n}</div>
                      {i < processSteps.length - 1 && <div className="w-0.5 flex-1 min-h-[24px] bg-gradient-to-b from-teal/30 to-transparent mt-1"></div>}
                    </div>
                    <div className="pb-7">
                      <div className={`font-body font-semibold text-sm mb-1 ${s.highlight ? 'text-teal-light' : 'text-white'}`}>{s.title}</div>
                      <div className="text-white/45 text-xs leading-relaxed">{s.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <span className="section-tag" style={{background:'rgba(201,168,76,0.15)',color:'#E8C97A'}}>Why Travizon</span>
              <h2 className="font-display text-4xl sm:text-5xl text-white font-semibold mt-1 mb-8">We Do It Right.</h2>
              <div className="flex flex-col gap-4">
                {[
                  {
                    title: '100% legal process',
                    desc: 'We never submit false documents or shortcuts. Every application is prepared for full embassy scrutiny.',
                    accent: '#10B981',
                    shadow: 'rgba(16,185,129,0.35)',
                    bg: 'linear-gradient(135deg,#10B981,#34D399)',
                    icon: (
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                        <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
                      </svg>
                    ),
                  },
                  {
                    title: 'Radical transparency',
                    desc: "We tell you exactly what you're paying for, your real chances, and what could go wrong — before you commit.",
                    accent: '#3B82F6',
                    shadow: 'rgba(59,130,246,0.35)',
                    bg: 'linear-gradient(135deg,#3B82F6,#60A5FA)',
                    icon: (
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                      </svg>
                    ),
                  },
                  {
                    title: 'Outcome-focused',
                    desc: 'Our success is measured by your visa approval — not the number of consultations booked.',
                    accent: '#F59E0B',
                    shadow: 'rgba(245,158,11,0.35)',
                    bg: 'linear-gradient(135deg,#F59E0B,#FBBF24)',
                    icon: (
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                        <circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/>
                      </svg>
                    ),
                  },
                  {
                    title: 'Full journey support',
                    desc: 'Visa. Admission. Accommodation. We are not done until you are settled — not just stamped.',
                    accent: '#8B5CF6',
                    shadow: 'rgba(139,92,246,0.35)',
                    bg: 'linear-gradient(135deg,#8B5CF6,#A78BFA)',
                    icon: (
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/>
                      </svg>
                    ),
                  },
                ].map(w => (
                  <div key={w.title} className="flex gap-4 items-start bg-white/[0.04] border border-white/8 rounded-2xl p-5 hover:bg-white/[0.07] hover:border-white/15 transition-all duration-300 group">
                    {/* Modern colored icon box */}
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform duration-300"
                      style={{ background: w.bg, boxShadow: `0 6px 18px ${w.shadow}` }}
                    >
                      {w.icon}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="font-body font-bold text-white text-sm">{w.title}</div>
                        {/* Colored accent dot */}
                        <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: w.accent }}></div>
                      </div>
                      <div className="text-white/45 text-xs leading-relaxed">{w.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── VALUE ADDED SERVICES PREVIEW ── */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <SectionHeader tag="Beyond the Visa" title="Value Added Services" subtitle="We support you long after your visa is approved — from airport pickup on Day 1 to bank accounts, SIM cards, and document attestation." />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
            {valueAddedServices.map(svc => {
              const iconKey = getIconKey(svc.title);
              const accent  = getAccentColor(iconKey);
              return (
                <button key={svc.title} onClick={() => go('value-added')}
                  className="group bg-white border border-gray-100 rounded-2xl p-5 text-left hover:shadow-[0_12px_40px_rgba(11,31,58,0.1)] hover:-translate-y-1 transition-all duration-300 overflow-hidden relative"
                  style={{ '--accent': accent }}>
                  {/* Top accent bar */}
                  <div className="absolute top-0 left-0 right-0 h-0.5 scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" style={{ background: accent }}/>
                  <div className="w-12 h-12 mb-3 drop-shadow-md">
                    <ValueAddedIcon type={iconKey} size={48} />
                  </div>
                  <div className="font-body font-semibold text-navy text-sm mb-1">{svc.title}</div>
                  <div className="text-gray-400 text-xs leading-relaxed line-clamp-2">{svc.desc.split('—')[0]}</div>
                </button>
              );
            })}
          </div>
          <div className="text-center">
            <button onClick={() => go('value-added')} className="btn-outline-dark">Explore All Value Added Services →</button>
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className="py-20 bg-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <SectionHeader tag="Client Stories" title="Real Results." subtitle="500+ families, students, and professionals have trusted Travizon Migration." />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {testimonials.map(t => <TestiCard key={t.name} {...t} />)}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <CTASection title="Ready to Begin Your Journey?" subtitle="Book a free consultation — in-person at Gulberg-3 Lahore, or via WhatsApp. No pressure, no commitment." setPage={setPage} />

      {/* ── FAQ PREVIEW ── */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <SectionHeader tag="Common Questions" title="FAQ" subtitle="Quick answers to the most common questions." />
          <div className="max-w-3xl mx-auto">
            {faqs.slice(0, 5).map(f => <FAQItem key={f.q} {...f} />)}
          </div>
          <div className="text-center mt-8">
            <button onClick={() => go('faq')} className="btn-outline-dark">View All FAQs →</button>
          </div>
        </div>
      </section>
    </div>
  );
}
